import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Alert,
  Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseButton from './Common/CloseButton';
import { useWebSocket } from './WebSocketContext';

const looksLikeHtml = (text) => {
  if (!text || typeof text !== 'string') return false;
  return /<\/?[a-z][\s\S]*>/i.test(text);
};

const renderStructuredBlock = (title, content) => {
  if (!content) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      <Box
        component="pre"
        sx={{
          backgroundColor: '#f5f5f5',
          borderRadius: 1,
          p: 1.5,
          maxHeight: 260,
          overflowY: 'auto',
          fontSize: '0.8rem'
        }}
      >
        {JSON.stringify(content, null, 2)}
      </Box>
    </Box>
  );
};

const tryParseJson = (value) => {
  if (typeof value !== 'string') return null;
  try { return JSON.parse(value); } catch { return null; }
};

const canonicalizeSchemaValue = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = tryParseJson(trimmed);
    return parsed !== null ? canonicalizeSchemaValue(parsed) : trimmed;
  }
  if (Array.isArray(value)) {
    return JSON.stringify(value.map((item) => {
      const normalized = canonicalizeSchemaValue(item);
      return normalized === null ? null : tryParseJson(normalized) ?? normalized;
    }));
  }
  if (typeof value === 'object') {
    try {
      const normalizedObject = Object.keys(value)
        .sort()
        .reduce((acc, key) => {
          const normalized = canonicalizeSchemaValue(value[key]);
          acc[key] = normalized === null ? null : tryParseJson(normalized) ?? normalized;
          return acc;
        }, {});
      return JSON.stringify(normalizedObject);
    } catch {
      return JSON.stringify(value);
    }
  }
  return String(value);
};

const buildIsolatedHtml = (innerHtml) => (
  `<!DOCTYPE html>\n<html>\n  <head>\n    <meta charset="utf-8" />\n    <style>` +
  `*{box-sizing:border-box;} html,body{margin:0;padding:0;width:100%;height:100%;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:12px;line-height:1.4;color:#333;} ` +
  `img,video,canvas,svg,iframe{max-width:100%;height:auto;} table{width:100%;border-collapse:collapse;} pre,code{white-space:pre-wrap;word-break:break-word;}` +
  `</style>\n  </head>\n  <body>` + (innerHtml || '') + `</body>\n</html>`
);

const renderResponsePreview = (title, responseData) => {
  if (!responseData) return null;

  let obj = null;
  if (typeof responseData === 'string') obj = tryParseJson(responseData);
  else if (typeof responseData === 'object') obj = responseData;

  const answerContent = obj && typeof obj === 'object'
    ? (obj.answer || '')
    : (typeof responseData === 'string' ? responseData : '');
  const answerFormat = obj && typeof obj === 'object'
    ? (obj.answerFormat || 'plaintext')
    : 'plaintext';

  if (!answerContent) return null;

  const lowerFmt = (answerFormat || '').toLowerCase();
  const isHtml = lowerFmt.includes('html') || lowerFmt.includes('javascript') || looksLikeHtml(answerContent);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      {isHtml ? (
        <Box sx={{ border: '1px solid rgba(87, 87, 176, 0.2)', borderRadius: 1, overflow: 'hidden' }}>
          <iframe
            title="widget-answer-preview"
            srcDoc={buildIsolatedHtml(answerContent)}
            style={{ width: '100%', height: '320px', border: '0' }}
          />
        </Box>
      ) : (
        <Box
          component="pre"
          sx={{
            backgroundColor: 'rgba(87, 87, 176, 0.02)',
            border: '1px solid rgba(87, 87, 176, 0.2)',
            borderRadius: 1,
            p: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          {answerContent}
        </Box>
      )}
    </Box>
  );
};

const formatTagReply = (value) => {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  const parsed = tryParseJson(value);
  if (parsed && typeof parsed === 'object') {
    return JSON.stringify(parsed, null, 2);
  }
  return String(value);
};

const renderTagList = (tags, t) => {
  if (!Array.isArray(tags) || tags.length === 0) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {t('widgetSnapshot.tagsSection', 'Tags')}
      </Typography>
      <Box
        component="ul"
        sx={{
          listStyle: 'disc',
          pl: 3,
          mb: 0
        }}
      >
        {tags.map((tag) => (
          <li key={`${tag.id || tag.variable || Math.random()}`}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {tag.variable || t('widgetSnapshot.untitled', 'Untitled')}{' '}
              <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                ({tag.description || t('widgetSnapshot.noDescription', 'No description')})
              </Typography>
            </Typography>
            <Typography
              variant="body2"
              sx={{
                backgroundColor: '#f7f7fb',
                border: '1px solid rgba(87, 87, 176, 0.15)',
                borderRadius: 1,
                p: 1,
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {formatTagReply(tag.reply ?? tag.value ?? '')}
            </Typography>
          </li>
        ))}
      </Box>
    </Box>
  );
};

const renderContextMetadata = (context, t) => {
  if (!context || typeof context !== 'object') return null;
  const entries = Object.entries(context);
  if (entries.length === 0) return null;

  return (
    <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, border: '1px solid rgba(85, 145, 255, 0.2)', backgroundColor: '#f4f8ff' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {t('widgetSnapshot.contextMetadata', 'Context Metadata')}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
        {entries.map(([key, value]) => (
          <Box
            key={key}
            sx={{
              p: 1,
              border: '1px solid rgba(92, 108, 224, 0.2)',
              borderRadius: 1,
              backgroundColor: '#ffffff',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 130, color: 'text.primary' }}>
              {key}
            </Typography>
            <Typography
              component="span"
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                flex: 1
              }}
            >
              {value === null || value === undefined
                ? '—'
                : (typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value))}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const renderReasoning = (snapshotData, t) => {
  const raw = snapshotData?.new_response;
  let parsed = null;

  if (raw) {
    if (typeof raw === 'string') {
      parsed = tryParseJson(raw);
    } else if (typeof raw === 'object') {
      parsed = raw;
    }
  }

  const reason = parsed && typeof parsed === 'object' ? parsed.reason : null;
  if (!reason) return null;

  return (
    <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, border: '1px solid rgba(75, 95, 210, 0.25)', backgroundColor: '#f9faff' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {t('widgetSnapshot.reasoning', 'Reasoning')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap', fontFamily: 'monospace', lineHeight: 1.5 }}>
        {reason}
      </Typography>
    </Box>
  );
};

const renderTimeEventSections = (snapshotData, t) => {
  return (
    <>
      {renderStructuredBlock(t('widgetSnapshot.eventMetadata', 'Event Metadata'), snapshotData?.timeevent)}

      {snapshotData?.timeevent?.eventnote && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('widgetSnapshot.note', 'Note')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
            {snapshotData.timeevent.eventnote}
          </Typography>
        </Box>
      )}

      {renderTagList(snapshotData?.tags, t)}

      {snapshotData?.event_payload && renderStructuredBlock(t('widgetSnapshot.eventData', 'Event Data'), snapshotData.event_payload)}
    </>
  );
};

const WidgetSnapshotDialog = ({ open, onClose, snapshotContext, formatTimestamp }) => {
  const { t } = useTranslation();
  const { sendMessage, subscribeToMessages } = useWebSocket();
  const [isReverting, setIsReverting] = useState(false);
  const [revertFeedback, setRevertFeedback] = useState(null);
  const [currentWidgetAnswer, setCurrentWidgetAnswer] = useState(null);
  const [isLoadingCurrentWidget, setIsLoadingCurrentWidget] = useState(false);
  const snapshotData = snapshotContext?.data;
  const snapshotType = snapshotContext?.type
    || (snapshotData?.widget ? 'widget' : snapshotData?.timeevent ? 'timeevent' : 'widget');
  const dialogTitle = snapshotType === 'timeevent'
    ? t('widgetSnapshot.timeEventTitle', 'Time Event Snapshot')
    : (snapshotData?.widget?.title
      ? `${snapshotData.widget.title} (${snapshotData.widget.variable || t('widgetSnapshot.noVariable', 'No variable')})`
      : t('widgetSnapshot.widgetTitle', 'Widget Snapshot'));
  const capturedLabel = t('widgetSnapshot.capturedAt', 'Captured');

  const parsedSnapshotResponse = useMemo(() => {
    const rawResponse = snapshotData?.new_response;
    if (!rawResponse) return null;
    if (typeof rawResponse === 'string') {
      const parsed = tryParseJson(rawResponse);
      return parsed && typeof parsed === 'object' ? parsed : null;
    }
    if (typeof rawResponse === 'object') {
      return rawResponse;
    }
    return null;
  }, [snapshotData?.new_response]);

  const snapshotSchemaValue = parsedSnapshotResponse?.schema_json ?? parsedSnapshotResponse?.schema;
  const hasSnapshotSchema = snapshotSchemaValue !== undefined && snapshotSchemaValue !== null;
  const widgetId = snapshotData?.widget?.id;
  const caseId = snapshotData?.widget?.caseid ?? snapshotContext?.log?.case_id;
  const widgetLogId = snapshotContext?.log?.id;
  const currentWidgetSchemaValue = currentWidgetAnswer?.schema_json ?? currentWidgetAnswer?.schema;
  const hasCurrentWidgetSchema = currentWidgetSchemaValue !== undefined && currentWidgetSchemaValue !== null;
  const doesSchemaMatch = useMemo(() => {
    if (!hasSnapshotSchema || !hasCurrentWidgetSchema) {
      return null;
    }
    return canonicalizeSchemaValue(snapshotSchemaValue) === canonicalizeSchemaValue(currentWidgetSchemaValue);
  }, [hasCurrentWidgetSchema, hasSnapshotSchema, currentWidgetSchemaValue, snapshotSchemaValue]);

  const revertDisabledReason = useMemo(() => {
    if (snapshotType !== 'widget') {
      return t('widgetSnapshot.revertDisabledNotWidget', 'Only widget snapshots can be reverted.');
    }
    if (!widgetId) {
      return t('widgetSnapshot.revertDisabledMissingWidget', 'This snapshot is missing the widget ID.');
    }
    if (!caseId) {
      return t('widgetSnapshot.revertDisabledMissingCase', 'This snapshot is missing the project ID.');
    }
    if (!widgetLogId) {
      return t('widgetSnapshot.revertDisabledMissingHistory', 'This snapshot is missing the widget history ID.');
    }
    if (!parsedSnapshotResponse) {
      return t('widgetSnapshot.revertDisabledInvalidSnapshot', 'This snapshot does not contain a valid widget response.');
    }
    if (!hasSnapshotSchema) {
      return t('widgetSnapshot.revertDisabledMissingSnapshotSchema', 'This snapshot cannot be reverted because it does not contain a data schema.');
    }
    if (isLoadingCurrentWidget) {
      return t('widgetSnapshot.revertDisabledLoadingCurrent', 'Checking compatibility with the current widget schema...');
    }
    if (!currentWidgetAnswer) {
      return t('widgetSnapshot.revertDisabledCurrentMissing', 'The current widget state could not be loaded to verify compatibility.');
    }
    if (!hasCurrentWidgetSchema) {
      return t('widgetSnapshot.revertDisabledCurrentSchemaMissing', 'The current widget does not expose a schema, so compatibility cannot be verified.');
    }
    if (doesSchemaMatch === false) {
      return t('widgetSnapshot.revertDisabledSchemaMismatch', 'This snapshot cannot be reverted because its data schema no longer matches the current widget schema.');
    }
    return '';
  }, [
    caseId,
    currentWidgetAnswer,
    doesSchemaMatch,
    hasCurrentWidgetSchema,
    hasSnapshotSchema,
    isLoadingCurrentWidget,
    parsedSnapshotResponse,
    snapshotType,
    t,
    widgetId,
    widgetLogId
  ]);

  const canRevert = !revertDisabledReason;

  const handleRevert = () => {
    if (!sendMessage || !canRevert) return;
    setIsReverting(true);
    setRevertFeedback(null);
    try {
      sendMessage({
        command: 'revertWidgetSnapshot',
        caseid: caseId,
        widget_id: widgetId,
        snapshot_log_id: widgetLogId
      });
    } catch (err) {
      setIsReverting(false);
      setRevertFeedback({
        severity: 'error',
        message: t('widgetSnapshot.revertError', 'Unable to revert the UI.')
      });
      console.error('WidgetSnapshotDialog: revert request failed', err);
    }
  };

  useEffect(() => {
    if (!subscribeToMessages) return undefined;
    const unsubscribe = subscribeToMessages(['revertWidgetSnapshot'], (message) => {
      const data = message.data || {};
      if (!widgetId) return;
      if (String(data.widget_id) !== String(widgetId)) return;
      if (caseId && String(data.caseid) !== String(caseId)) return;
      setIsReverting(false);
      if (data.status === 'success' || data.success === true) {
        setRevertFeedback({
          severity: 'success',
          message: data.message || t('widgetSnapshot.revertSuccess', 'Widget UI reverted to this snapshot.')
        });
      } else {
        setRevertFeedback({
          severity: 'error',
          message: data.message || t('widgetSnapshot.revertError', 'Unable to revert the UI.')
        });
      }
    });
    return unsubscribe;
  }, [subscribeToMessages, widgetId, caseId, t]);

  useEffect(() => {
    if (!open || !subscribeToMessages || !sendMessage || snapshotType !== 'widget' || !caseId || !widgetId) {
      return undefined;
    }

    setCurrentWidgetAnswer(null);
    setIsLoadingCurrentWidget(true);
    const unsubscribe = subscribeToMessages(['getWidgets'], (message) => {
      const payload = message?.data;
      const payloadCaseId = payload?.caseid ?? payload?.case_id ?? payload?.project_id;
      if (payloadCaseId !== undefined && payloadCaseId !== null
        && String(payloadCaseId) !== String(caseId)) {
        return;
      }
      if (payload?.status === 'pending') {
        return;
      }

      const widgets = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.widgets)
          ? payload.widgets
          : [];

      const matchedWidget = widgets.find((item) => String(item?.id) === String(widgetId));
      setCurrentWidgetAnswer(matchedWidget?.answer || null);
      setIsLoadingCurrentWidget(false);
    });

    sendMessage({
      command: 'getWidgets',
      caseid: caseId
    });

    return unsubscribe;
  }, [open, subscribeToMessages, sendMessage, snapshotType, caseId, widgetId]);

  useEffect(() => {
    if (!open) {
      setIsReverting(false);
      setRevertFeedback(null);
      setCurrentWidgetAnswer(null);
      setIsLoadingCurrentWidget(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {dialogTitle}
          </Typography>
          {snapshotContext?.log?.action_name && (
            <Typography variant="caption" color="text.secondary">
              {snapshotContext.log.action_name}
            </Typography>
          )}
          {snapshotData?.snapshot_taken_at && (
            <Typography variant="caption" color="text.secondary">
              {capturedLabel} {formatTimestamp(snapshotData.snapshot_taken_at)}
            </Typography>
          )}
        </Box>
        <CloseButton size="small" onClick={onClose} />
      </DialogTitle>
      <DialogContent dividers>
        {snapshotType === 'widget' && (
          <>
            {snapshotData?.widget?.description && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('widgetSnapshot.description', 'Description')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {snapshotData.widget.description}
                </Typography>
              </Box>
            )}

            {renderReasoning(snapshotData, t)}

            {renderResponsePreview(t('widgetSnapshot.widgetSection', 'Widget'), snapshotData?.new_response)}
            {renderResponsePreview(t('widgetSnapshot.previousResponse', 'Previous Response'), snapshotData?.previous_response)}
            {renderStructuredBlock(t('widgetSnapshot.previousResponseMeta', 'Previous Response Metadata'), snapshotData?.previous_response_meta)}

            {renderContextMetadata(snapshotData?.context, t)}
          </>
        )}

        {snapshotType === 'timeevent' && renderTimeEventSections(snapshotData, t)}

        {revertFeedback && (
          <Box sx={{ mt: 2 }}>
            <Alert
              severity={revertFeedback.severity}
              onClose={() => setRevertFeedback(null)}
              variant="filled"
            >
              {revertFeedback.message}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 1 }}>
        <Button onClick={onClose} variant="outlined">
          {t('common.close', 'Close')}
        </Button>
        <Tooltip title={isReverting ? '' : revertDisabledReason} disableHoverListener={!revertDisabledReason || isReverting}>
          <span>
            <Button
              onClick={handleRevert}
              variant="contained"
              disabled={!canRevert || isReverting}
            >
              {isReverting
                ? t('widgetSnapshot.revertButtonBusy', 'Reverting...')
                : t('widgetSnapshot.revertButton', 'Revert to this UI')}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default WidgetSnapshotDialog;
